import React, { FC, useRef, useState, useEffect } from 'react'
import { makeStyles, Typography, Tooltip as MuiTooltip, withStyles, Fade, Button, Theme } from '@material-ui/core'
import { ArrowBack, MailOutline } from '@material-ui/icons'
import Lottie, { LottieRefCurrentProps } from 'lottie-react'
import { isMobile } from 'react-device-detect'

import { E3DDemoNameVals, GameFlowSteps } from 'types'

import { ReactComponent as FactoryImg } from 'assets/factory/factory.svg'
import fan from 'assets/factory/fan.json'
import camera from 'assets/factory/camera.json'
import car from 'assets/factory/car.json'
import computer from 'assets/factory/computer.json'
import light from 'assets/factory/light.json'
import robot from 'assets/factory/robot.json'
import warehouse from 'assets/factory/warehouse.json'

interface LottieElementProps {
  data: object
  title: string
  text: string
  lottieClass: string
}

const Tooltip = withStyles((theme: Theme) => ({
  tooltip: {
    backgroundColor: '#fff',
    border: '1px solid #000',
    color: '#000',
    fontSize: '1rem',
    borderRadius: 0,
    fontFamily: 'Verizon-Bold',
    boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.2)',
    left: -30,
    padding: '2rem',
    [theme.breakpoints.down('sm')]: {
      padding: '0.25rem',
      fontSize: '0.875rem'
    }
  },
  arrow: {
    color: '#fff',
    '&::before': {
      border: '1px solid #000',
      backgroundColor: '#fff',
      boxSizing: 'border-box'
    }
  }
}))(MuiTooltip)

const LottieElement: FC<LottieElementProps> = ({ data, lottieClass, title, text }) => {
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false)
  const ref = useRef<LottieRefCurrentProps | null>(null)
  const toggleTooltip = () => {
    setTooltipOpen(open => !open)
  }
  return (
    <Tooltip
      arrow
      placement='top'
      title={
        <>
          <Typography variant='h5' paragraph style={{ fontSize: '1.125rem' }}>
            {title}
          </Typography>
          <Typography paragraph style={{ fontSize: '.875rem' }}>
            {text}
          </Typography>
        </>
      }
    >
      <div className={lottieClass}>
        <Lottie
          lottieRef={ref}
          animationData={data}
          autoplay={false}
          loop={true}
          onMouseEnter={() => ref && ref.current && ref.current.play()}
          onMouseLeave={() => ref && ref.current && ref.current.stop()}
          onClick={toggleTooltip}
        />
      </div>
    </Tooltip>
  )
}

interface FactoryProps {
  setScene: (sceneTo: GameFlowSteps) => void
}

export const Factory: FC<FactoryProps> = ({ setScene }) => {
  const classes = useStyles()
  const [show, setShow] = useState<boolean>(false)

  useEffect(() => {
    const timeout = setTimeout(() => {
      setShow(true)
    }, 2000)

    return () => clearTimeout(timeout)
  }, [])

  const actionClicked = (action: 'explore' | 'connect' | 'next' | 'demo') => {
    switch (action) {
      case 'explore':
        setScene(GameFlowSteps.BackToExplore)
        break
      case 'connect':
        window.postMessage('{"command":"connect", "param":"factory"}', '*')
        break
      case 'demo':
        window.postMessage(JSON.stringify({ command: 'demos', param: E3DDemoNameVals.roboEvolution }), '*')
        break
    }
  }

  return (
    <Fade in={show}>
      <div className={classes.root}>
        <div className={classes.headingContainer}>
          <Typography variant='h2' className={classes.heading}>
            Reliable wireless allows full factory
            <br />
            digitalisation for digital twin.
          </Typography>
          <Typography paragraph className={classes.subHeading}>
            Connecting all assets for situational awareness and IIoT analytics.
            <br />
            <strong>{isMobile ? 'Long press' : 'Hover over'} the icons below for more information.</strong>
          </Typography>
        </div>

        <div className={classes.container}>
          <FactoryImg className={classes.factory} />
          {/* Fan */}
          <LottieElement
            data={fan}
            lottieClass={classes.fan}
            title='Enterprise micro-grid'
            text='An intelligent and dynamic microgrid optimises power consumption to manage and minimise usage spikes.'
          />
          {/* light */}
          <LottieElement
            data={light}
            lottieClass={classes.light}
            title='Sensors and environment monitoring for quality'
            text='A condition monitoring system can self-adjust production in line with optimal environmnet for quality assurance.'
          />
          {/* computer */}
          <LottieElement
            data={computer}
            lottieClass={classes.computer}
            title='Factory operation center'
            text='Plan and control production in real-time by predicting unplanned events and generating automatic response based on analysed data.'
          />
          {/* car */}
          <LottieElement
            data={car}
            lottieClass={classes.car}
            title='AGV/AIV/AMR connectivity'
            text='An automated and synchonised AGV system maximises efficency, and can raise alerts for anomalies.'
          />
          {/* camera */}
          <LottieElement
            data={camera}
            lottieClass={classes.camera}
            title='Video for quality monitoring'
            text='Video feeds can leverage machine learning to analyse and detect any deviations in production for quality assurance.'
          />
          {/* robot */}
          <LottieElement
            data={robot}
            lottieClass={classes.robot}
            title='Machine monitoring'
            text='Interconnected machines can work together seamlessly, enabling increased productivity, flexibility and safety for workers.'
          />
          {/* warehouse */}
          <LottieElement
            data={warehouse}
            lottieClass={classes.warehouse}
            title='Asset tracking'
            text='A system of sensors can monitor conditions throughout the supply chain process, and track analyses of multiple conditions required for product.'
          />
        </div>

        <div className={classes.contentActionBox}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => {
              actionClicked('explore')
            }}
            style={{ marginRight: '2rem' }}
          >
            Back
          </Button>

          <Button startIcon={<MailOutline />} onClick={() => actionClicked('connect')}>
            Send a message
          </Button>

          <Button
            endIcon={<ArrowBack className={classes.forwardArrow} />}
            onClick={() => {
              actionClicked('demo')
            }}
            style={{ marginLeft: '2rem' }}
          >
            Next Demo
          </Button>
        </div>
      </div>
    </Fade>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'absolute',
    width: '75%',
    height: '100%',
    top: '15%',
    left: '50%',
    transform: 'translateX(-50%)',
    overflow: 'auto'
  },
  headingContainer: {
    color: '#000',
    width: '75%',
    marginBottom: '2rem'
  },
  heading: {
    fontSize: '2.125rem',
    marginBottom: '1rem',
    [theme.breakpoints.down('sm')]: {
      marginTop: '1rem',
      fontSize: '1.5rem'
    }
  },
  subHeading: {
    fontSize: '1.125rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.875rem'
    }
  },
  container: {
    width: '80%',
    margin: '0 auto',
    position: 'relative'
  },
  factory: {
    width: '100%'
  },
  fan: {
    position: 'absolute',
    top: '-9%',
    left: '7%',
    width: '37%'
  },
  light: {
    position: 'absolute',
    top: '48%',
    left: '50%',
    width: '3%'
  },
  computer: {
    position: 'absolute',
    top: '27%',
    left: '72%',
    width: '14%'
  },
  car: {
    position: 'absolute',
    top: '79%',
    left: '25%',
    width: '11%'
  },
  camera: {
    position: 'absolute',
    top: '66%',
    left: '39%',
    width: '6.5%'
  },
  robot: {
    position: 'absolute',
    top: '71%',
    left: '45%',
    width: '18%'
  },
  warehouse: {
    position: 'absolute',
    top: '57%',
    left: '69%',
    width: '24%'
  },
  forwardArrow: {
    position: 'relative',
    WebkitTransform: 'rotate(180deg)',
    transform: 'rotate(180deg)',
    cursor: 'pointer'
  },
  contentActionBox: {
    [theme.breakpoints.down('sm')]: {
      marginBottom: '1rem'
    }
  }
}))
